import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const ArrayInput = ({ label, items, setItems, name }) => (
  <div className="space-y-3">
    <label className="block text-sm font-bold text-text-secondary">{label}</label>
    {items.map((item, idx) => (
      <div key={idx} className="flex items-center gap-2">
        <input
          type="text"
          value={item}
          onChange={(e) => {
            const newItems = [...items];
            newItems[idx] = e.target.value;
            setItems(newItems);
          }}
          className="flex-1 bg-white/5 border border-white/10 rounded-xl px-4 py-2 text-text-primary focus:border-accent"
        />
        <button
          type="button"
          onClick={() => setItems(items.filter((_, i) => i !== idx))}
          className="w-10 h-10 rounded-xl bg-red-500/10 hover:bg-red-500/20 flex flex-shrink-0 items-center justify-center text-red-400 transition-smooth border border-red-500/10"
        >
          <Icon name="Trash2" size={16} />
        </button>
      </div>
    ))}
    <button
      type="button"
      onClick={() => setItems([...items, ''])}
      className="text-sm font-semibold text-accent hover:text-accent-light flex items-center gap-1 mt-2"
    >
      <Icon name="Plus" size={14} /> Add Item
    </button>
    <input type="hidden" name={name} value={JSON.stringify(items.filter(i => i.trim() !== ''))} />
  </div>
);

const ResumeManager = ({ adminKey }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
  const [activeTab, setActiveTab] = useState('summary');
  const [formType, setFormType] = useState('summary');
  const [formAchievements, setFormAchievements] = useState([]);
  const [formTechnologies, setFormTechnologies] = useState([]);
  const [formImages, setFormImages] = useState([]);

  const tabs = [
    { id: 'summary', label: 'Summary' },
    { id: 'competency', label: 'Competencies' },
    { id: 'experience', label: 'Experience' },
    { id: 'education', label: 'Education' },
    { id: 'certification', label: 'Certifications' },
    { id: 'achievement', label: 'Achievements' },
    { id: 'reference', label: 'References' },
    { id: 'fun_fact', label: 'Fun Facts' },
    { id: 'download', label: 'Downloads' }
  ];

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const res = await fetch('https://spaceportfolio-api.rajansudhan0.workers.dev/api/resume', { 
        cache: 'no-store',
        headers: { 'X-Admin-Key': adminKey }
      });
      const data = await res.json();
      if (Array.isArray(data)) {
        setItems(data);
      } else {
        setItems([]);
      }
    } catch (err) {
      console.error('Failed to fetch resume items:', err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setStatusMessage({ type: '', text: '' });

    const formData = new FormData(e.target);
    const itemData = {
      type: formData.get('type'),
      title: formData.get('title'),
      organization: formData.get('organization'),
      location: formData.get('location'),
      duration: formData.get('duration'),
      badge: formData.get('badge'),
      description: formData.get('description'),
      detailedContent: formData.get('detailedContent'),
      date_value: formData.get('date_value'),
      display_order: parseInt(formData.get('display_order')) || 0,
    };
    
    // Append a unique suffix to the slug to bypass the database UNIQUE constraint on this column
    const rawSlug = formData.get('slug') || '';
    itemData.slug = rawSlug ? `${rawSlug}___${Date.now()}` : `___${Date.now()}`;

    const rawAch = formData.get('achievements');
    const rawTech = formData.get('technologies');
    try { itemData.achievements = rawAch ? JSON.parse(rawAch) : []; } catch (e) { itemData.achievements = []; }
    try { itemData.technologies = rawTech ? JSON.parse(rawTech) : []; } catch (e) { itemData.technologies = []; }
    itemData.images = JSON.stringify(formImages.filter(u => u.trim()));

    if (editingItem) itemData.id = editingItem.id;

    try {
      const res = await fetch('https://spaceportfolio-api.rajansudhan0.workers.dev/api/resume', {
        method: editingItem ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Admin-Key': adminKey
        },
        body: JSON.stringify(itemData)
      });

      if (res.ok) {
        setStatusMessage({ type: 'success', text: `Item successfully ${editingItem ? 'updated' : 'added'}!` });
        fetchItems();
        setTimeout(() => {
          setIsModalOpen(false);
          setStatusMessage({ type: '', text: '' });
        }, 1000);
      } else {
        const error = await res.json();
        throw new Error(error.error || 'Failed to save');
      }
    } catch (err) {
      setStatusMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resume item?')) return;
    try {
      const res = await fetch(`https://spaceportfolio-api.rajansudhan0.workers.dev/api/resume/${id}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });
      if (res.ok) fetchItems();
      else throw new Error('Failed to delete');
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const openForm = (type, item = null) => {
    setEditingItem(item);
    setFormType(type);
    setStatusMessage({ type: '', text: '' });
    setFormAchievements(item?.achievements || []);
    setFormTechnologies(item?.technologies || []);
    const rawImgs = item?.images;
    let imgs = [];
    if (rawImgs) { try { imgs = JSON.parse(rawImgs); } catch { imgs = rawImgs.split(',').map(s=>s.trim()); } }
    setFormImages(Array.isArray(imgs) ? imgs.filter(Boolean) : []);
    setIsModalOpen(true);
  };

  const getFilteredItems = (type) => {
    if (type === 'achievement') {
      return items.filter(item => ['achievement', 'outreach', 'speaking'].includes(item.type));
    }
    return items.filter(item => item.type === type);
  };



  const renderSection = (tab) => {
    const sectionItems = getFilteredItems(tab.id);

    // ── LinkedIn-style grouped rendering for Experience tab ─────────────
    const renderExperienceGrouped = () => {
      const map = new Map();
      sectionItems.forEach(item => {
        const key = (item.organization || '').trim().toLowerCase() || `__noorg__${item.id}`;
        if (!map.has(key)) {
          map.set(key, { orgName: item.organization || '—', logo: item.badge?.startsWith('http') ? item.badge : null, items: [] });
        }
        map.get(key).items.push(item);
      });
      const groups = Array.from(map.values());

      return (
        <div className="space-y-5">
          {groups.map((group, gIdx) => (
            // Every company (1 or more roles) uses a unified container
            <div key={gIdx} className="border border-white/10 rounded-2xl overflow-hidden bg-white/[0.01]">
              {/* Group header */}
              <div className="bg-white/5 border-b border-white/10 px-5 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {group.logo ? (
                    <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center p-0.5 overflow-hidden flex-shrink-0">
                      <img src={group.logo} alt="" className="w-full h-full object-contain" />
                    </div>
                  ) : (
                    <div className="w-8 h-8 bg-white/5 border border-white/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Building2" size={14} className="text-text-muted" />
                    </div>
                  )}
                  <div>
                    <span className="font-bold text-text-primary">{group.orgName}</span>
                    <span className="ml-2 bg-accent/20 text-accent-light text-xs font-semibold px-2 py-0.5 rounded-full">
                      {group.items.length} {group.items.length === 1 ? 'role' : 'roles'}
                    </span>
                  </div>
                </div>
                {/* Quick-add role button (now available for ALL companies) */}
                <button
                  onClick={() => {
                    openForm('experience');
                    // Pre-fill org name after modal opens (next tick)
                    setTimeout(() => {
                      const orgInput = document.querySelector('input[name="organization"]');
                      if (orgInput) { 
                        orgInput.value = group.orgName; 
                        orgInput.dispatchEvent(new Event('input', { bubbles: true })); 
                      }
                    }, 150);
                  }}
                  className="flex items-center gap-1.5 text-xs font-semibold text-accent hover:text-accent-light bg-accent/10 hover:bg-accent/20 border border-accent/20 px-3 py-1.5 rounded-full transition-smooth"
                  title={`Add another role at ${group.orgName}`}
                >
                  <Icon name="Plus" size={12} /> Add Role
                </button>
              </div>

              {/* Individual role cards */}
              <div className="divide-y divide-white/5">
                {group.items.map((item, rIdx) => (
                  <div key={item.id} className="bg-white/3 hover:bg-white/8 px-5 py-4 flex justify-between items-start transition-smooth group">
                    <div className="flex items-start gap-3">
                      {/* Timeline dot (only show if multiple roles exist) */}
                      {group.items.length > 1 && (
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${rIdx === 0 ? 'bg-accent shadow-[0_0_6px_rgba(14,165,233,0.5)]' : 'bg-white/20'}`} />
                      )}
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-2">
                          <span className="text-accent font-mono text-xs">#{item.display_order || '0'}</span>
                          <h4 className="font-semibold text-text-primary">{item.title}</h4>
                        </div>
                        <p className="text-text-muted text-xs italic">{item.duration || item.date_value}</p>
                        {item.description && <p className="text-text-secondary text-xs line-clamp-1 mt-1">{item.description}</p>}
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button onClick={() => openForm(item.type, item)} className="w-8 h-8 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-white transition-smooth border border-white/5"><Icon name="Edit2" size={14} /></button>
                      <button onClick={() => handleDelete(item.id)} className="w-8 h-8 rounded-xl bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center text-red-400 transition-smooth border border-red-500/10"><Icon name="Trash2" size={14} /></button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      );
    };
    // ────────────────────────────────────────────────────────────────────

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <span className="w-2 h-8 bg-accent rounded-full" />
            Manage {tab.label}
          </h2>
          <button
            onClick={() => openForm(tab.id)}
            className="bg-accent hover:bg-accent/90 text-white px-4 py-2 rounded-xl transition-smooth font-medium flex items-center gap-2"
          >
            <Icon name="Plus" size={18} /> Add {tab.label} Item
          </button>
        </div>

        <div className="grid gap-4">
          {loading ? (
            <div className="p-8 text-center text-text-muted bg-white/5 rounded-2xl border border-white/10 animate-pulse">
              Loading {tab.label.toLowerCase()}...
            </div>
          ) : sectionItems.length === 0 ? (
            <div className="p-8 text-center text-text-muted bg-white/5 rounded-2xl border border-white/10">
              No {tab.label.toLowerCase()} items found. Click the button above to add one.
            </div>
          ) : tab.id === 'experience' ? (
            renderExperienceGrouped()
          ) : (
            sectionItems.map(item => (
              <div key={item.id} className="bg-white/5 border border-white/10 p-5 rounded-2xl hover:bg-white/10 transition-smooth group flex justify-between items-start">
                <div className="space-y-1">
                  <div className="flex items-center gap-3">
                    <span className="text-accent font-mono text-sm">#{item.display_order || '0'}</span>
                    <h3 className="font-bold text-text-primary text-lg">{item.title}</h3>
                    {item.badge && (
                      item.badge.startsWith('http') ? (
                        <div className="w-6 h-6 bg-white rounded flex items-center justify-center p-0.5 overflow-hidden flex-shrink-0">
                          <img src={item.badge} alt="Logo" className="w-full h-full object-contain" />
                        </div>
                      ) : (
                        <span className="bg-primary-900/40 text-primary-300 text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full">{item.badge}</span>
                      )
                    )}
                  </div>
                  <p className="text-text-secondary text-sm">{item.organization} {item.location && `• ${item.location}`}</p>
                  <p className="text-text-muted text-xs italic">{item.duration}</p>
                  {item.description && <p className="text-text-secondary text-sm line-clamp-2 mt-2">{item.description}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => openForm(item.type, item)} className="w-10 h-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-text-secondary hover:text-white transition-smooth border border-white/5"><Icon name="Edit2" size={16} /></button>
                  <button onClick={() => handleDelete(item.id)} className="w-10 h-10 rounded-xl bg-red-500/10 hover:bg-red-500/20 flex items-center justify-center text-red-400 transition-smooth border border-red-500/10"><Icon name="Trash2" size={16} /></button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Tabs UI */}
      <div className="flex overflow-x-auto pb-4 gap-2 custom-scrollbar border-b border-white/10">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-6 py-3 border-b-2 font-bold whitespace-nowrap transition-smooth ${
              activeTab === tab.id 
                ? 'border-accent text-accent'
                : 'border-transparent text-text-muted hover:border-white/20 hover:text-text-secondary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Active Section */}
      <div className="min-h-[400px]">
        {tabs.map(tab => activeTab === tab.id && <div key={tab.id}>{renderSection(tab)}</div>)}
      </div>

      {/* Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" onClick={() => !isSaving && setIsModalOpen(false)}></div>
          <div className="bg-surface border border-white/10 rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto relative z-10 shadow-3xl">
            <div className="sticky top-0 bg-surface/90 backdrop-blur-md px-8 py-5 border-b border-white/10 flex justify-between items-center z-20">
              <h3 className="font-heading font-bold text-2xl text-text-primary capitalize">
                {editingItem ? `Edit ${formType}` : `Add New ${formType}`}
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-text-muted hover:text-white transition-smooth">
                <Icon name="X" size={24} />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-8 space-y-8">
              <input type="hidden" name="type" value={formType} />
              
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-text-secondary uppercase tracking-wider mb-2">Display Order</label>
                  <input type="number" name="display_order" defaultValue={editingItem?.display_order || ''} placeholder="1" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-text-secondary uppercase tracking-wider mb-2">Internal Sort Date (Optional)</label>
                  <input type="text" name="date_value" defaultValue={editingItem?.date_value || ''} placeholder="2024-05" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                </div>
              </div>

              <div className="space-y-6 pt-4 border-t border-white/10">
                 {(() => {
                    switch (formType) {
                      case 'summary':
                        return (
                          <>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Main Heading *</label>
                                <input type="text" name="title" required defaultValue={editingItem?.title} placeholder="Aspiring Space Researcher..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Total Experience Text</label>
                                <input type="text" name="duration" defaultValue={editingItem?.duration} placeholder="3+ Years of Experience" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Detailed Summary</label>
                                <textarea name="description" rows={5} defaultValue={editingItem?.description} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent resize-y"></textarea>
                            </div>
                            <ArrayInput label="Key Highlights" items={formAchievements} setItems={setFormAchievements} name="achievements" />
                          </>
                        );
                      case 'competency':
                        return (
                          <>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Skill Group Name *</label>
                                <input type="text" name="title" required defaultValue={editingItem?.title} placeholder="Data Science & ML" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Type</label>
                                <select name="badge" defaultValue={editingItem?.badge || 'technical'} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent">
                                  <option value="technical">Technical Skill</option>
                                  <option value="soft">Soft Skill</option>
                                </select>
                              </div>
                            </div>
                            <ArrayInput label="List Skills" items={formTechnologies} setItems={setFormTechnologies} name="technologies" />
                          </>
                        );
                      case 'experience':
                      case 'education':
                      case 'certification':
                        return (
                          <>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Title / Program *</label>
                                <input type="text" name="title" required defaultValue={editingItem?.title} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Organization / School *</label>
                                <input type="text" name="organization" required defaultValue={editingItem?.organization} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Location</label>
                                <input type="text" name="location" defaultValue={editingItem?.location} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">
                                  {formType === 'certification' ? 'External Credential Link (URL)' : 'Duration'}
                                </label>
                                <input type="text" name="duration" defaultValue={editingItem?.duration} placeholder={formType === 'certification' ? 'https://credential.net/...' : ''} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Institute / Company Logo URL (Optional)</label>
                                <input type="url" name="badge" defaultValue={editingItem?.badge} placeholder="https://example.com/logo.png" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Badge (e.g. Intern, Top 1%) (Optional)</label>
                                <input type="text" name="slug" defaultValue={editingItem?.slug ? editingItem.slug.split('___')[0] : ''} placeholder="Top Performer" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Short Description</label>
                                <textarea name="description" rows={3} defaultValue={editingItem?.description} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent resize-y"></textarea>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">
                                  Detailed Content (HTML for Read More page)
                                </label>
                                <textarea name="detailedContent" rows={6} defaultValue={editingItem?.detailedContent} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent font-mono text-sm resize-y"></textarea>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <ArrayInput label="Achievements" items={formAchievements} setItems={setFormAchievements} name="achievements" />
                              </div>
                              <div>
                                <ArrayInput label="Technologies Used" items={formTechnologies} setItems={setFormTechnologies} name="technologies" />
                              </div>
                            </div>
                          </>
                        );
                      case 'achievement':
                      case 'speaking':
                      case 'outreach':
                        return (
                          <>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Title *</label>
                                <input type="text" name="title" required defaultValue={editingItem?.title} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Type</label>
                                <select name="type" defaultValue={editingItem?.type || 'achievement'} onChange={(e) => setFormType(e.target.value)} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent">
                                  <option value="achievement">Award / Honor</option>
                                  <option value="outreach">Outreach Activity</option>
                                  <option value="speaking">Speaking Engagement</option>
                                </select>
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Issuing Organisation</label>
                                <input type="text" name="organization" defaultValue={editingItem?.organization} placeholder="NASA, IEEE..." className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Location</label>
                                <input type="text" name="location" defaultValue={editingItem?.location} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Date / Year</label>
                                <input type="text" name="date_value" defaultValue={editingItem?.date_value} placeholder="November 2025" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Category Badge (e.g. Outreach, Research)</label>
                                <input type="text" name="slug" defaultValue={editingItem?.slug ? editingItem.slug.split('___')[0] : ''} placeholder="Recognition" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-bold text-text-secondary mb-2">Logo / Badge Image URL (optional)</label>
                              <input type="url" name="badge" defaultValue={editingItem?.badge} placeholder="https://example.com/logo.png" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                            </div>
                            <div>
                              <label className="block text-sm font-bold text-text-secondary mb-2">Short Description</label>
                              <textarea name="description" rows={3} defaultValue={editingItem?.description} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent resize-y" />
                            </div>
                            <div>
                              <label className="block text-sm font-bold text-text-secondary mb-2">Detailed Content (HTML, shown on detail page)</label>
                              <textarea name="detailedContent" rows={5} defaultValue={editingItem?.detailedContent} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent font-mono text-sm resize-y" />
                            </div>
                            <ArrayInput label="Key Highlights / Bullet Points" items={formAchievements} setItems={setFormAchievements} name="achievements" />
                            <ArrayInput label="Tags" items={formTechnologies} setItems={setFormTechnologies} name="technologies" />
                            <ArrayInput label="📷 Image URLs (slideshow on detail page)" items={formImages} setItems={setFormImages} name="_images_ignored" />
                          </>
                        );
                      case 'reference':
                        return (
                          <>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Referee Name *</label>
                                <input type="text" name="title" required defaultValue={editingItem?.title} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Position</label>
                                <input type="text" name="location" defaultValue={editingItem?.location} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Organization</label>
                                <input type="text" name="organization" defaultValue={editingItem?.organization} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Relationship</label>
                                <input type="text" name="duration" defaultValue={editingItem?.duration} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Testimonial Quote</label>
                                <textarea name="description" rows={4} defaultValue={editingItem?.description} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent resize-y"></textarea>
                            </div>
                            <ArrayInput label="Contact Details (Order: Email, Phone, LinkedIn URL)" items={formTechnologies} setItems={setFormTechnologies} name="technologies" />
                          </>
                        );
                      case 'download':
                        return (
                          <>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">File Format (e.g. PDF) *</label>
                                <input type="text" name="title" required defaultValue={editingItem?.title} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">File Size</label>
                                <input type="text" name="badge" defaultValue={editingItem?.badge} placeholder="245 KB" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Short Description</label>
                                <input type="text" name="description" defaultValue={editingItem?.description} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                            </div>
                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Icon (Lucide name)</label>
                                <input type="text" name="organization" defaultValue={editingItem?.organization} placeholder="FileText" className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                              <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">Save FileName</label>
                                <input type="text" name="slug" defaultValue={editingItem?.slug} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                              </div>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-text-secondary mb-2">File Public path/URL *</label>
                                <input type="text" name="detailedContent" required defaultValue={editingItem?.detailedContent} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-text-primary focus:border-accent" />
                            </div>
                          </>
                        );
                      default:
                        return null;
                    }
                 })()}
              </div>

              {/* Action Buttons */}
              <div className="sticky bottom-0 bg-surface/90 backdrop-blur-md pt-5 border-t border-white/10 flex items-center justify-between mt-8">
                  <div className="text-sm">
                    {statusMessage.text && (
                      <span className={statusMessage.type === 'error' ? 'text-red-400 font-bold' : 'text-success font-bold'}>
                        {statusMessage.text}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-4">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="px-8 py-3 rounded-xl border border-white/10 hover:bg-white/5 text-text-secondary font-bold transition-smooth">Cancel</button>
                    <button type="submit" disabled={isSaving} className="px-8 py-3 rounded-xl bg-accent hover:bg-accent/90 text-white font-bold transition-smooth flex items-center gap-2 shadow-lg shadow-accent/20">
                        {isSaving && <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>}
                        <span className="capitalize">Save {formType}</span>
                    </button>
                  </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumeManager;
